<script setup lang="ts">
    import { http } from '@/shared/httpWrapper';
    import { computed, ref, watch, watchEffect } from 'vue';
    import { useRouter } from 'vue-router';
    import { saveAs } from 'file-saver';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import {
        getTRDocumentListLink,
        getTRRevisionContentEditorLink,
        TRRevisionContentEditorProps
    } from '@/shared/helpers/routing';
    import {
        BatchDocumentCode,
        createChangeDocAndCommit,
        finalizeTrRevision,
        getTRDocumentContentLibraries,
        TRDocumentContentLibrary,
        trDocumentMakeLinksExcel
    } from '@/shared/helpers/api';
    import { useTRDocumentStore } from './TRDocumentStore';
    import { TRDocumentRecord, TRRevisionRecord } from './TRDocumentQueries';
    import { useDialog } from 'buefy';
    import useShow from '@/shared/composables/useShow';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import DateTimePicker from '@/shared/components/DateTimePicker.vue';
    import Spinner from '@/shared/components/Spinner.vue';
    import TRRevisionContentItemEditor from './TRRevisionContentItemEditor.vue';
    import CodeEditSaveProgress from '@/shared/components/CodeEditSaveProgress.vue';

    const props = defineProps<TRRevisionContentEditorProps>();

    const store = useTRDocumentStore();
    const dialog = useDialog();
    const router = useRouter();
    const { showError, showInfo } = useShow();

    const selectedTRDocument = ref<TRDocumentRecord | undefined>();
    const selectedTRRevision = ref<TRRevisionRecord | undefined>();
    const contentLibraryList = ref<TRDocumentContentLibrary[]>([]);

    watch(() => props.trDocumentIdentity, async () => {
        if (props.trDocumentIdentity) {
            await store.init();
            const docMatch = store.getTRDocument(props.trDocumentIdentity);
            selectedTRDocument.value = docMatch?.document;
            selectedTRRevision.value = docMatch?.revisions.find(x => x.identity === props.trRevisionIdentity);

            contentLibraryList.value = await getTRDocumentContentLibraries(http, props.trDocumentIdentity);
        }
    }, { immediate: true });

    watchEffect(() => {
        if (selectedTRDocument.value?.identity !== props.trDocumentIdentity) {
            updateRoute();
        }
    });

    watchEffect(() => {
        if (selectedTRRevision.value?.identity !== props.trRevisionIdentity) {
            updateRoute();
        }
    });

    function updateRoute() {
        router.push(getTRRevisionContentEditorLink({
            trDocumentIdentity: selectedTRDocument.value?.identity,
            trRevisionIdentity: selectedTRRevision.value?.identity
        }));
    }

    const revisionList = computed(() => {
        if (!selectedTRDocument.value)
            return [];
        return store.trRevisionList.filter(x => x.trDocument === selectedTRDocument.value?.identity);
    });

    function onConfirm(message: string, callback: () => Promise<void>) {
        dialog.confirm({
            title: 'Please confirm',
            message: message,
            type: 'is-danger',
            hasIcon: true,
            cancelText: 'No',
            confirmText: 'Yes',
            onConfirm: callback
        });
    }

    async function doCommit(codeSetName: string, code: BatchDocumentCode): Promise<boolean> {
        try {
            const result = await createChangeDocAndCommit(http, codeSetName, [code]);
            showInfo(result.data.message);
            return true;
        } catch (err) {
            showError(err);
            return false;
        }
    }

    async function onSetDataFinished(): Promise<void> {
        onConfirm('Are you sure you want to set edit done?', async () => {
            const ok = await doCommit(store.trRevisionCodeSetName!, {
                Name: selectedTRRevision.value!.name,
                TRDocument: selectedTRRevision.value!.trDocument,
                AreDataFinished: true
            });
            if (ok) {
                store.setDataFinished(selectedTRRevision.value!.identity);
            }
        });
    }

    async function onSetQueriesFinished(): Promise<void> {
        onConfirm('Are you sure you want to set queries as finished?', async () => {
            const ok = await doCommit(store.trRevisionCodeSetName!, {
                Name: selectedTRRevision.value!.name,
                TRDocument: selectedTRRevision.value!.trDocument,
                AreQueriesFinished: true
            });
            if (ok) {
                store.setQueriesFinished(selectedTRRevision.value!.identity);
            }
        });
    }

    function onSetIssuedDate(issuedDate: string): void {
        onConfirm('Are you sure you want to set issued date?', async () => {
            const ok = await doCommit(store.trRevisionCodeSetName!, {
                Name: selectedTRRevision.value!.name,
                TRDocument: selectedTRRevision.value!.trDocument,
                IssueDate: issuedDate
            });
            if (ok) {
                selectedTRRevision.value!.issueDate = issuedDate;
                store.setIssueDate(selectedTRRevision.value!.identity, issuedDate);
            }
        });
    }

    async function onSetURLsFinished(): Promise<void> {
        onConfirm('Are you sure you want to set URLs as finished?', async () => {
            const ok = await doCommit(store.trRevisionCodeSetName!, {
                Name: selectedTRRevision.value!.name,
                TRDocument: selectedTRRevision.value!.trDocument,
                AreURLsFinished: true
            });
            if (ok) {
                store.setURLsFinished(selectedTRRevision.value!.identity);
            }
        });
    }

    const finalizeReleaseId = ref<number | null>(null);
    const isFinalizing = ref<boolean>(false);
    const isCallingFinalize = ref<boolean>(false);

    async function onSetAllFinished(): Promise<void> {
        onConfirm('Are you sure you want to finalize the revision?', async () => doSetAllFinished());
    }

    async function doSetAllFinished(): Promise<void> {
        try {
            isCallingFinalize.value = true;
            const response = await finalizeTrRevision(http, selectedTRRevision.value!.identity);
            isFinalizing.value = true;
            finalizeReleaseId.value = response.data;
        } catch (error: any) {
            dialog.alert({
                title: error.response.data ?? 'An error occurred while finalizing the revision.',
                message: 'A refresh of the page is recommended to see the current state of the revision.',
                type: 'is-danger',
                hasIcon: true,
                icon: 'refresh',
                iconPack: 'mdi',
                ariaRole: 'alertdialog',
                ariaModal: true
            });
            // Reset finalizing state in case of error - handled by 'onFinishedFinalize' otherwise
            isFinalizing.value = false;
            finalizeReleaseId.value = null;
        } finally {
            isCallingFinalize.value = false;
        }
    }

    function onFinishFinalize(): void {
        store.setAllFinished(selectedTRRevision.value!.identity);
        isFinalizing.value = false;
    }

    async function onGenerateUrlList(): Promise<void> {
        try {
            const trDocument = selectedTRDocument.value!;
            const trRevision = selectedTRRevision.value!;

            const blob = await trDocumentMakeLinksExcel(http, trRevision.identity);

            if (blob) {
                const fileName = `${trDocument.name} ${trRevision.name} URL list.xlsx`;
                saveAs(blob, fileName);
                showInfo('URLs downloaded');
            } else {
                showError('Something went wrong producing Excel file');
            }
        } catch (err) {
            showError(err);
        }
    }
</script>

<template>
    <div>
        <router-link :to="getTRDocumentListLink()">
            &laquo; To TR document list
        </router-link>
    </div>
    <spinner :loading="store.initializing">
        <div class="mt-5">
            <div class="panel">
                <b-field grouped>
                    <b-field label="TR document">
                        <b-select v-model="selectedTRDocument">
                            <option
                                v-for="doc in store.trDocumentList"
                                :value="doc">
                                {{ doc.name }} / {{ doc.technologyDescription }} / {{ doc.validityContextDescription }}
                            </option>
                        </b-select>
                    </b-field>
                    <b-field label="Revision">
                        <b-select v-model="selectedTRRevision">
                            <option
                                v-for="rev in revisionList"
                                :value="rev">
                                {{ rev.name }}
                            </option>
                        </b-select>
                    </b-field>
                    <b-field
                        v-if="selectedTRRevision"
                        label="Revision state"
                        grouped>
                        <b-field>
                            <bool-element
                                :value="selectedTRRevision.areDataInitialized"
                                size="large" />
                            {{ selectedTRRevision.areDataInitialized ? 'Initialized' : 'Not initialized' }}
                        </b-field>
                        <b-field>
                            <bool-element
                                :value="selectedTRRevision.areDataFinished"
                                size="large" />
                            {{ selectedTRRevision.areDataFinished ? 'Edit finished' : 'Edit not finished' }}
                        </b-field>
                        <b-field>
                            <bool-element
                                :value="selectedTRRevision.areQueriesFinished"
                                size="large" />
                            {{ selectedTRRevision.areQueriesFinished ? 'SQLs finished' : 'SQLs not finished' }}
                        </b-field>
                        <b-field>
                            <bool-element
                                :value="selectedTRRevision.areURLsFinished"
                                size="large" />
                            {{ selectedTRRevision.areURLsFinished ? 'URLs finished' : 'URLs not finished' }}
                        </b-field>
                        <b-field>
                            <bool-element
                                :value="selectedTRRevision.isAllFinished"
                                size="large" />
                            {{ selectedTRRevision.isAllFinished ? 'All finished (all work done)' : 'Not all finished (work remains)' }}
                        </b-field>
                    </b-field>
                </b-field>
                <b-field grouped>
                    <b-field
                        v-if="selectedTRRevision"
                        label="Finalize actions"
                        grouped>
                        <b-field>
                            <p class="control">
                                <b-button
                                    v-require-can-edit-code="{ libraryName: 'TRRevision' }"
                                    type="is-primary"
                                    :disabled="selectedTRRevision.areDataFinished"
                                    @click="onSetDataFinished()">
                                    Edit done
                                </b-button>
                            </p>
                        </b-field>
                        <b-field>
                            <p class="control">
                                <b-button
                                    v-require-can-edit-code="{ libraryName: 'TRRevision' }"
                                    type="is-primary"
                                    :disabled="selectedTRRevision.areQueriesFinished"
                                    @click="onSetQueriesFinished()">
                                    SQLs done
                                </b-button>
                            </p>
                        </b-field>
                        <b-field>
                            <p class="control">
                                <b-button
                                    v-require-can-edit-code="{ libraryName: 'TRRevision' }"
                                    type="is-primary"
                                    :disabled="selectedTRRevision.areURLsFinished"
                                    @click="onSetURLsFinished()">
                                    URLs done
                                </b-button>
                            </p>
                        </b-field>
                        <b-field>
                            <p class="control">
                                <b-button
                                    v-require-can-edit-code="{ libraryName: 'TRRevision' }"
                                    type="is-primary"
                                    :loading="isCallingFinalize"
                                    :disabled="selectedTRRevision.isAllFinished"
                                    @click="onSetAllFinished()">
                                    Finalize revision
                                </b-button>
                            </p>
                        </b-field>
                        <b-field>
                            <p class="control">
                                <b-button
                                    type="is-info"
                                    @click="onGenerateUrlList()">
                                    Generate URL list
                                </b-button>
                            </p>
                        </b-field>
                    </b-field>

                    <b-field
                        v-if="selectedTRRevision"
                        label="Issued date">
                        <date-time-picker
                            :date-only="true"
                            :input="selectedTRRevision.issueDate"
                            :stack-date-and-time-input="true"
                            @dt-picker="onSetIssuedDate">
                            <p class="control">
                                <b-button
                                    v-require-can-edit-code="{ libraryName: 'TRRevision' }"
                                    type="is-primary"
                                    @click="onSetIssuedDate">
                                    Set
                                </b-button>
                            </p>
                        </date-time-picker>
                    </b-field>
                </b-field>
            </div>

            <t-r-revision-content-item-editor
                v-if="selectedTRRevision && selectedTRDocument"
                :content-library-list="contentLibraryList"
                :document="selectedTRDocument"
                :revision="selectedTRRevision" />
        </div>
    </spinner>
    <code-edit-save-progress
        :close-enabled="false"
        :release-id="finalizeReleaseId"
        :save-process-started="isFinalizing"
        @success="onFinishFinalize" />
</template>

<style scoped>
.panel {
    border: solid 1px var(--eq-autumn-storm-1);
    padding: 10px;
    background-color: var(--eq-autumn-storm-3);
}
</style>
